import React, { useState, useRef, useEffect } from 'react';
import { Target, TrendingUp, AlertCircle, CheckCircle, Info, Home, Users, DollarSign, Lightbulb, HelpCircle } from 'lucide-react';
import { agedCareWiseMarkUrl } from '../../components/branding/index.tsx';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import type {
    ServiceBuilderSelections,
    ServiceCostsBreakdown,
    ServiceBuilderContributionRates,
    LastChangedServiceInfo,
} from './ServiceBuilderSection.tsx';

type InsightStage = 'welcome' | 'servicesSelected' | 'budgetModerate' | 'budgetHigh' | 'budgetExceeded';

export interface ServiceBuilderInsightPanelProps {
    factFind: DraftFactFind;
    selectedServices: ServiceBuilderSelections;
    activeTab: 'client' | 'partner';
    serviceCosts: ServiceCostsBreakdown;
    combinedCosts?: ServiceCostsBreakdown | null;
    contributionRates: ServiceBuilderContributionRates;
    clientBudget: number;
    partnerBudget: number;
    totalBudget: number;
    currentCashflow: number;
    projectedLegacy?: number;
    isPartnered: boolean;
    hasLinkedAgedCareStrategy?: boolean;
    lastChangedService?: LastChangedServiceInfo | null;
}

interface DragState {
    startX: number;
    startY: number;
    startPosX: number;
    startPosY: number;
}

// Service Builder Insight Panel - Provides guidance on building service plans
const ServiceBuilderInsightPanel: React.FC<ServiceBuilderInsightPanelProps> = ({
    factFind,
    selectedServices,
    activeTab,
    serviceCosts,
    combinedCosts,
    contributionRates,
    clientBudget,
    partnerBudget,
    totalBudget,
    currentCashflow,
    projectedLegacy,
    isPartnered,
    hasLinkedAgedCareStrategy,
    lastChangedService
}) => {

    // Simple state for UI only
    const [showPanel, setShowPanel] = useState(true);
    const [isDragging, setIsDragging] = useState(false);
    const [panelPos, setPanelPos] = useState({ x: 60, y: 120 });
    const dragRef = useRef<DragState | null>(null);
    const contentRef = useRef<HTMLDivElement | null>(null); // Ref for auto-scrolling

    // Helper function to format currency
    const formatCurrency = (value: number | null | undefined): string => {
        if (!value || isNaN(value)) return '$0';
        const roundedValue = Math.round(value);
        return `${roundedValue < 0 ? '-' : ''}$${Math.abs(roundedValue).toLocaleString()}`;
    };

    // Get user's name
    const userName = factFind?.completerFirstName || factFind?.client?.firstName || 'there';

    // Auto-scroll to top when services change
    useEffect(() => {
        if (lastChangedService && contentRef.current) {
            contentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [lastChangedService]);

    // Calculate current stage/context
    const getCurrentStage = (): InsightStage => {
        const hasClientServices = Object.keys(selectedServices?.client || {}).length > 0;
        const hasPartnerServices = Object.keys(selectedServices?.partner || {}).length > 0;
        const hasAnyServices = isPartnered ? (hasClientServices || hasPartnerServices) : Object.keys(selectedServices || {}).length > 0;

        if (!hasAnyServices) {
            return 'welcome';
        }

        // Check budget utilization
        const currentServicesUsed = isPartnered
            ? (activeTab === 'client' ? serviceCosts.total : (selectedServices?.partner ? Object.keys(selectedServices.partner).length > 0 ? serviceCosts.total : 0 : 0))
            : serviceCosts.total;
        const currentBudget = isPartnered ? (activeTab === 'client' ? clientBudget : partnerBudget) : clientBudget;
        const budgetUsed = currentBudget > 0 ? (currentServicesUsed / currentBudget) * 100 : 0;

        if (budgetUsed > 100) {
            return 'budgetExceeded';
        } else if (budgetUsed > 80) {
            return 'budgetHigh';
        } else if (budgetUsed > 50) {
            return 'budgetModerate';
        } else if (budgetUsed > 0) {
            return 'servicesSelected';
        }

        return 'welcome';
    };

    interface StrategyCardProps {
        title: React.ReactNode;
        icon?: React.ComponentType<{ size?: number; style?: React.CSSProperties }>;
        iconColor: 'blue' | 'green' | 'purple' | 'amber' | 'teal';
        children: React.ReactNode;
    }

    // StrategyCard component for modern card styling
    const StrategyCard: React.FC<StrategyCardProps> = ({ title, icon: Icon, iconColor, children }) => {
        const colorMap: Record<StrategyCardProps['iconColor'], { bg: string; border: string; icon: string }> = {
            blue: { bg: '#f5f7fa', border: '#e2e8f0', icon: '#2a4365' },
            green: { bg: '#f5f7fa', border: '#e2e8f0', icon: '#2a4365' },
            purple: { bg: '#f5f7fa', border: '#e2e8f0', icon: '#2a4365' },
            amber: { bg: '#f5f7fa', border: '#e2e8f0', icon: '#2a4365' },
            teal: { bg: '#f5f7fa', border: '#e2e8f0', icon: '#2a4365' }
        };
        const colors = colorMap[iconColor] || colorMap.blue;
        return (
            <div style={{
                marginBottom: '20px',
                padding: '16px',
                background: colors.bg,
                border: `1px solid ${colors.border}`,
                borderRadius: '12px',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    marginBottom: '12px',
                    paddingBottom: '12px',
                    borderBottom: `1px solid ${colors.border}`
                }}>
                    {Icon && <Icon size={20} style={{ color: colors.icon }} />}
                    <h3 style={{
                        margin: 0,
                        fontSize: '16px',
                        fontWeight: 600,
                        color: '#1e293b'
                    }}>{title}</h3>
                </div>
                <div style={{ fontSize: '14px', lineHeight: '1.7', color: '#475569' }}>
                    {children}
                </div>
            </div>
        );
    };
    
    // Generate insights based on current stage
    const getInsightContent = () => {
        const currentStage = getCurrentStage();
        const currentServicesUsed = isPartnered 
            ? (activeTab === 'client' ? serviceCosts.total : serviceCosts.total)
            : serviceCosts.total;
        const currentBudget = isPartnered ? (activeTab === 'client' ? clientBudget : partnerBudget) : clientBudget;
        const budgetUsed = currentBudget > 0 ? (currentServicesUsed / currentBudget) * 100 : 0;
        const remainingBudget = currentBudget - currentServicesUsed;
        const totalContribution = isPartnered ? combinedCosts?.contribution?.total || 0 : serviceCosts?.contribution?.total || 0;
        const remainingCashflow = currentCashflow - totalContribution;
        
        // Calculate service distribution
        const totalServices = isPartnered ? combinedCosts?.total || 0 : serviceCosts?.total || 0;
        const clinicalServices = isPartnered ? combinedCosts?.totalClinical || 0 : serviceCosts?.totalClinical || 0;
        const independenceServices = isPartnered ? combinedCosts?.totalIndependence || 0 : serviceCosts?.totalIndependence || 0;
        const everydayServices = isPartnered ? combinedCosts?.totalEveryday || 0 : serviceCosts?.totalEveryday || 0;
        
        const clinicalPercent = totalServices > 0 ? (clinicalServices / totalServices) * 100 : 0;
        const independencePercent = totalServices > 0 ? (independenceServices / totalServices) * 100 : 0;
        const everydayPercent = totalServices > 0 ? (everydayServices / totalServices) * 100 : 0;
        
        switch (currentStage) {
            case 'welcome':
                return (
                    <>
                        <p style={{ marginBottom: '16px' }}>Welcome <strong>{userName}</strong>! I'm here to help you build your Support at Home service plan.</p>
                        
                        <StrategyCard title="How Service Builder Works" icon={Target} iconColor="blue">
                            <p style={{ marginBottom: '8px' }}><strong>Step 1:</strong> Select services from the three categories:</p>
                            <ul style={{ marginLeft: '20px', marginTop: '8px', marginBottom: '8px' }}>
                                <li><strong>Clinical Care</strong> - No contribution required (0%)</li>
                                <li><strong>Independence Services</strong> - {((contributionRates?.independence || 0) * 100).toFixed(0)}% contribution</li>
                                <li><strong>Everyday Living Services</strong> - {((contributionRates?.everydayLiving || 0) * 100).toFixed(0)}% contribution</li>
                            </ul>
                            <p style={{ marginBottom: '8px' }}><strong>Step 2:</strong> Enter quantities and frequency for each service</p>
                            <p style={{ marginBottom: '8px' }}><strong>Step 3:</strong> Review your budget utilization and contribution costs</p>
                        </StrategyCard>
                        
                        {isPartnered && !hasLinkedAgedCareStrategy && (
                            <StrategyCard title="Couple Budget Allocation Tips" icon={Users} iconColor="teal">
                                <p style={{ marginBottom: '8px' }}>For couples, you each have your own budget:</p>
                                <ul style={{ marginLeft: '20px', marginTop: '8px', marginBottom: '8px' }}>
                                    <li><strong>{factFind?.client?.firstName || 'Client'}</strong>: {formatCurrency(clientBudget)}/year</li>
                                    <li><strong>{factFind?.partner?.firstName || 'Partner'}</strong>: {formatCurrency(partnerBudget)}/year</li>
                                    <li><strong>Combined Total</strong>: {formatCurrency(totalBudget)}/year</li>
                                </ul>
                                <p style={{ marginTop: '12px', marginBottom: '8px' }}><strong>Tip:</strong> Services like home maintenance, gardening, or cleaning can be allocated to either person's budget. Consider allocating shared services to the person with more available budget.</p>
                            </StrategyCard>
                        )}
                        {isPartnered && hasLinkedAgedCareStrategy && (
                            <StrategyCard title="One partner in residential care" icon={Home} iconColor="teal">
                                <p style={{ marginBottom: '8px' }}>The selected financial position has one partner in residential aged care. Their Support at Home package is not available here.</p>
                                <p style={{ marginBottom: '8px' }}>Only <strong>{factFind?.partner?.firstName || 'Partner'}'s</strong> budget is used in the Service Builder: <strong>{formatCurrency(partnerBudget)}/year</strong>. Use the Partner tab to allocate services for the person at home.</p>
                            </StrategyCard>
                        )}
                        
                        <StrategyCard title="Select Financial Position" icon={Lightbulb} iconColor="amber">
                            <p style={{ marginBottom: '8px' }}>At the top of the Service Builder, you'll see a dropdown to <strong>"Select Financial Position"</strong>. This powerful feature allows you to:</p>
                            <ul style={{ marginLeft: '20px', marginTop: '8px', marginBottom: '8px' }}>
                                <li><strong>Use Baseline:</strong> See service costs based on your current financial position (no optimization strategies applied)</li>
                                <li><strong>Compare Strategies:</strong> Select any saved strategy from "Optimise Your Plan" to see how that financial position affects your service costs and contributions</li>
                                <li><strong>Trial Different Approaches:</strong> Switch between strategies to compare how different optimization approaches impact your service plan affordability</li>
                            </ul>
                            <p style={{ marginTop: '12px', marginBottom: '0' }}>This helps you understand how your financial optimization choices affect your Support at Home service costs and contribution rates.</p>
                        </StrategyCard>
                        
                        <StrategyCard title="Understanding Contribution Rates" icon={Info} iconColor="purple">
                            <p style={{ marginBottom: '8px' }}>Your contribution rates are based on your financial position:</p>
                            <ul style={{ marginLeft: '20px', marginTop: '8px', marginBottom: '8px' }}>
                                <li><strong>Clinical Care</strong>: Always 0% - fully funded by government</li>
                                <li><strong>Independence Services</strong>: {((contributionRates?.independence || 0) * 100).toFixed(0)}% - you pay this portion</li>
                                <li><strong>Everyday Living</strong>: {((contributionRates?.everydayLiving || 0) * 100).toFixed(0)}% - you pay this portion</li>
                            </ul>
                            <p style={{ marginTop: '12px', marginBottom: '0' }}>The government pays the remaining percentage for Independence and Everyday Living services.</p>
                        </StrategyCard>
                        
                        <StrategyCard title="Service Block Selection" icon={HelpCircle} iconColor="amber">
                            <p style={{ marginBottom: '8px' }}><strong>Important:</strong> Service quantities are entered as whole blocks (hours, trips, meals, etc.).</p>
                            <ul style={{ marginLeft: '20px', marginTop: '8px', marginBottom: '8px' }}>
                                <li>Services are typically provided in minimum blocks (e.g., 1 hour minimum for nursing, 1 trip for transport)</li>
                                <li>You cannot book partial blocks (e.g., 0.5 hours) - only whole numbers are accepted</li>
                                <li>If you need less than a full block, discuss options with your service provider</li>
                                <li>Frequency options (daily/weekly/monthly) help you plan your service schedule</li>
                            </ul>
                            <p style={{ marginTop: '12px', marginBottom: '0', fontSize: '13px', fontStyle: 'italic' }}>Note: Exact service block requirements may vary by provider. Please confirm minimum service blocks with your chosen provider.</p>
                        </StrategyCard>
                        
                        <p style={{ marginTop: '16px', marginBottom: '0' }}><strong>Next step:</strong> Start selecting services that meet your needs. I'll guide you as you build your plan!</p>
                    </>
                );
                
            case 'servicesSelected':
            case 'budgetModerate':
            case 'budgetHigh':
                // Generate dynamic service-specific messaging if a service was just changed
                const getDynamicServiceMessage = () => {
                    // Always prioritize specific service message if a service was just changed
                    if (lastChangedService && lastChangedService.quantity > 0) {
                        // Calculate service cost and contribution
                        const convertToMonthly = (quantity: number, freqType: string): number => {
                            if (!quantity || quantity <= 0) return 0;
                            switch(freqType) {
                                case 'daily': return quantity * 30;
                                case 'weekly': return quantity * 4;
                                case 'monthly': return quantity;
                                default: return quantity;
                            }
                        };
                        
                        const monthlyQuantity = convertToMonthly(lastChangedService.quantity, lastChangedService.frequency);
                        const annualCost = lastChangedService.medianPrice * monthlyQuantity * 12;
                        
                        // Determine contribution rate based on category
                        let contributionRate = 0;
                        if (lastChangedService.category === 'Clinical Care') {
                            contributionRate = 0;
                        } else if (lastChangedService.category === 'Independence Services') {
                            contributionRate = contributionRates?.independence || 0;
                        } else if (lastChangedService.category === 'Everyday Living Services') {
                            contributionRate = contributionRates?.everydayLiving || 0;
                        }
                        
                        const contribution = annualCost * contributionRate;
                        const frequencyText = lastChangedService.frequency === 'daily' ? 'per day' : 
                                             lastChangedService.frequency === 'weekly' ? 'per week' : 'per month';
                        
                        return `You've selected <strong>${lastChangedService.name}</strong> of ${lastChangedService.quantity} ${lastChangedService.unit.toLowerCase()} ${frequencyText}. This is an estimated cost of <strong>${formatCurrency(annualCost)}</strong> per year, of which your contribution will be <strong>${formatCurrency(contribution)}</strong> per year.`;
                    }
                    
                    // Only show generic budget message if no service was just changed
                    if (currentStage === 'budgetHigh') {
                        return `You've used ${budgetUsed.toFixed(1)}% of your budget - you're making good use of your available funding!`;
                    } else if (currentStage === 'budgetModerate') {
                        return `You've used ${budgetUsed.toFixed(1)}% of your budget - a good balance!`;
                    }
                    return `Great start! You've selected services totaling <strong>${formatCurrency(currentServicesUsed)}</strong>.`;
                };
                
                return (
                    <>
                        <p dangerouslySetInnerHTML={{ __html: getDynamicServiceMessage() }} />
                        
                        <StrategyCard title="Budget Status" icon={TrendingUp} iconColor="green">
                            <p style={{ marginBottom: '8px' }}>• <strong>Budget Used</strong>: {budgetUsed.toFixed(1)}% ({formatCurrency(currentServicesUsed)} of {formatCurrency(currentBudget)})</p>
                            <p style={{ marginBottom: '8px' }}>• <strong>Remaining Budget</strong>: <strong>{formatCurrency(remainingBudget)}</strong></p>
                            <p style={{ marginBottom: '0' }}>• <strong>Your Annual Contribution</strong>: <strong>{formatCurrency(totalContribution)}</strong></p>
                        </StrategyCard>
                        
                        {currentStage === 'servicesSelected' && (
                            <>
                                {isPartnered && !hasLinkedAgedCareStrategy && (
                                    <StrategyCard title="Couple Allocation Guidance" icon={Users} iconColor="teal">
                                        <p style={{ marginBottom: '8px' }}>You're currently viewing <strong>{activeTab === 'client' ? factFind?.client?.firstName || 'Client' : factFind?.partner?.firstName || 'Partner'}'s</strong> services.</p>
                                        {activeTab === 'client' && Object.keys(selectedServices?.partner || {}).length === 0 && (
                                            <p style={{ marginTop: '12px', marginBottom: '8px' }}><strong>Tip:</strong> After completing {factFind?.client?.firstName || 'Client'}'s services, switch to the Partner tab to allocate their budget. You can copy services between tabs if both need similar support.</p>
                                        )}
                                        {activeTab === 'partner' && Object.keys(selectedServices?.client || {}).length > 0 && (
                                            <p style={{ marginTop: '12px', marginBottom: '8px' }}><strong>Tip:</strong> Consider using the "Copy Client's Services" button if you both need similar support. This saves time and ensures consistency.</p>
                                        )}
                                        {isPartnered && (clinicalServices > 0 || independenceServices > 0 || everydayServices > 0) && (
                                            <p style={{ marginTop: '12px', marginBottom: '0' }}><strong>Shared Services:</strong> Services like home maintenance, cleaning, or gardening benefit both of you. Consider allocating these to the person with more available budget.</p>
                                        )}
                                    </StrategyCard>
                                )}
                                {isPartnered && hasLinkedAgedCareStrategy && (
                                    <StrategyCard title="Residential care scenario" icon={Home} iconColor="teal">
                                        <p style={{ marginBottom: '0' }}>Only the partner at home has an available budget here. You're viewing <strong>{activeTab === 'client' ? factFind?.client?.firstName || 'Client' : factFind?.partner?.firstName || 'Partner'}'s</strong> services. Use the Partner tab to allocate services for the person at home.</p>
                                    </StrategyCard>
                                )}
                                
                                <StrategyCard title="Service Distribution" icon={CheckCircle} iconColor="blue">
                                    {clinicalPercent > 0 && <p style={{ marginBottom: '4px' }}>• <strong>Clinical Care</strong>: {formatCurrency(clinicalServices)} ({clinicalPercent.toFixed(1)}%) - <strong>No contribution</strong></p>}
                                    {independencePercent > 0 && <p style={{ marginBottom: '4px' }}>• <strong>Independence Services</strong>: {formatCurrency(independenceServices)} ({independencePercent.toFixed(1)}%)</p>}
                                    {everydayPercent > 0 && <p style={{ marginBottom: '0' }}>• <strong>Everyday Living</strong>: {formatCurrency(everydayServices)} ({everydayPercent.toFixed(1)}%)</p>}
                                </StrategyCard>
                                
                                <StrategyCard title="Cashflow Impact" icon={DollarSign} iconColor="green">
                                    <p style={{ marginBottom: '8px' }}>• <strong>Current Cashflow</strong>: {formatCurrency(currentCashflow)}/year</p>
                                    <p style={{ marginBottom: '8px' }}>• <strong>Less: Your Contribution</strong>: {formatCurrency(totalContribution)}/year</p>
                                    <p style={{ marginBottom: '0' }}>• <strong>Remaining Cashflow</strong>: <strong style={{ color: remainingCashflow >= 0 ? '#10b981' : '#ef4444' }}>{formatCurrency(remainingCashflow)}</strong>/year</p>
                                </StrategyCard>
                                
                                {remainingBudget > currentBudget * 0.3 && (
                                    <p style={{ marginTop: '16px', marginBottom: '0' }}><strong>Tip:</strong> You still have {formatCurrency(remainingBudget)} available. Consider adding more services if needed, or save the remaining budget for future needs.</p>
                                )}
                            </>
                        )}
                        
                        {currentStage === 'budgetModerate' && (
                            <>
                                {isPartnered && (
                                    <StrategyCard title="Couple Budget Optimization" icon={Users} iconColor="teal">
                                        <p style={{ marginBottom: '8px' }}>You have a good balance of budget utilization. Consider:</p>
                                        <ul style={{ marginLeft: '20px', marginTop: '8px', marginBottom: '8px' }}>
                                            <li>Allocating shared services (home maintenance, cleaning) to the person with more remaining budget</li>
                                            <li>Reviewing if both partners need the same services or if one can support the other</li>
                                            <li>Saving some budget for future needs or seasonal services</li>
                                        </ul>
                                    </StrategyCard>
                                )}
                                
                                <StrategyCard title="Service Plan Quality" icon={Target} iconColor="blue">
                                    <p style={{ marginBottom: '8px' }}>Your current service plan includes:</p>
                                    {clinicalPercent > 0 && <p style={{ marginBottom: '4px' }}>• <strong>Clinical Care</strong>: {formatCurrency(clinicalServices)} ({clinicalPercent.toFixed(1)}%)</p>}
                                    {independencePercent > 0 && <p style={{ marginBottom: '4px' }}>• <strong>Independence Services</strong>: {formatCurrency(independenceServices)} ({independencePercent.toFixed(1)}%)</p>}
                                    {everydayPercent > 0 && <p style={{ marginBottom: '0' }}>• <strong>Everyday Living</strong>: {formatCurrency(everydayServices)} ({everydayPercent.toFixed(1)}%)</p>}
                                    <p style={{ marginTop: '12px', marginBottom: '0' }}>This is a well-balanced plan that addresses multiple care needs.</p>
                                </StrategyCard>
                                
                                <StrategyCard title="Financial Impact" icon={DollarSign} iconColor="green">
                                    <p style={{ marginBottom: '8px' }}>• <strong>Remaining Cashflow</strong>: <strong style={{ color: remainingCashflow >= 0 ? '#10b981' : '#ef4444' }}>{formatCurrency(remainingCashflow)}</strong>/year</p>
                                    {projectedLegacy !== undefined && (
                                        <p style={{ marginBottom: '0' }}>• <strong>Projected Legacy (5 years)</strong>: {formatCurrency(projectedLegacy)}</p>
                                    )}
                                </StrategyCard>
                            </>
                        )}
                        
                        {currentStage === 'budgetHigh' && (
                            <>
                                {isPartnered && (
                                    <StrategyCard title="Couple Budget Management" icon={Users} iconColor="teal">
                                        <p style={{ marginBottom: '8px' }}>You're using most of your available budget. For couples, consider:</p>
                                        <ul style={{ marginLeft: '20px', marginTop: '8px', marginBottom: '8px' }}>
                                            <li>If one partner has more remaining budget, they could take on shared services like home maintenance</li>
                                            <li>Review if any services can be reduced in frequency rather than removed</li>
                                            <li>Consider if some services are essential vs. nice-to-have</li>
                                        </ul>
                                    </StrategyCard>
                                )}
                                
                                <StrategyCard title="Optimization Tips" icon={Lightbulb} iconColor="blue">
                                    <p style={{ marginBottom: '8px' }}>To maximize your remaining budget:</p>
                                    <ul style={{ marginLeft: '20px', marginTop: '8px', marginBottom: '8px' }}>
                                        <li>Focus on Clinical Care services (0% contribution) if you need more support</li>
                                        <li>Review Independence Services - these have a {((contributionRates?.independence || 0) * 100).toFixed(0)}% contribution rate</li>
                                        <li>Consider if Everyday Living services can be adjusted - these have a {((contributionRates?.everydayLiving || 0) * 100).toFixed(0)}% contribution rate</li>
                                    </ul>
                                </StrategyCard>
                                
                                <StrategyCard title="Financial Impact" icon={DollarSign} iconColor="green">
                                    <p style={{ marginBottom: '8px' }}>• <strong>Remaining Cashflow</strong>: <strong style={{ color: remainingCashflow >= 0 ? '#10b981' : '#ef4444' }}>{formatCurrency(remainingCashflow)}</strong>/year</p>
                                    {remainingCashflow < 0 && (
                                        <p style={{ marginTop: '12px', marginBottom: '0', color: '#ef4444' }}><strong>Note:</strong> Your contribution creates a cashflow shortfall. This can be funded from your liquid assets, but will impact your legacy estimate.</p>
                                    )}
                                </StrategyCard>
                            </>
                        )}
                    </>
                );
                
            case 'budgetExceeded':
                return (
                    <>
                        <p>Your selected services exceed your available budget by <strong>{formatCurrency(Math.abs(remainingBudget))}</strong>.</p>
                        
                        <StrategyCard title="Budget Exceeded" icon={AlertCircle} iconColor="amber">
                            <p style={{ marginBottom: '8px' }}>• <strong>Budget Available</strong>: {formatCurrency(currentBudget)}</p>
                            <p style={{ marginBottom: '8px' }}>• <strong>Services Selected</strong>: {formatCurrency(currentServicesUsed)}</p>
                            <p style={{ marginBottom: '0', color: '#ef4444' }}>• <strong>Over Budget</strong>: {formatCurrency(Math.abs(remainingBudget))}</p>
                        </StrategyCard>
                        
                        {isPartnered && (
                            <StrategyCard title="Couple Budget Rebalancing" icon={Users} iconColor="teal">
                                <p style={{ marginBottom: '8px' }}>For couples, you have options:</p>
                                <ul style={{ marginLeft: '20px', marginTop: '8px', marginBottom: '8px' }}>
                                    <li><strong>Reallocate Services:</strong> Move some services to your partner's budget if they have available capacity</li>
                                    <li><strong>Reduce Quantities:</strong> Reduce frequency or quantity of non-essential services</li>
                                    <li><strong>Prioritize:</strong> Focus on Clinical Care (0% contribution) and essential Independence Services first</li>
                                    <li><strong>Contact Provider:</strong> Discuss with your service provider about options for exceeding budget</li>
                                </ul>
                            </StrategyCard>
                        )}
                        
                        <StrategyCard title="How to Fix This" icon={Target} iconColor="blue">
                            <p style={{ marginBottom: '8px' }}><strong>Option 1: Reduce Service Quantities</strong></p>
                            <p style={{ marginLeft: '20px', marginBottom: '8px' }}>Reduce the frequency or quantity of services you've selected. For example, if you have cleaning 4 times per month, try 2 times per month.</p>
                            
                            <p style={{ marginBottom: '8px' }}><strong>Option 2: Remove Non-Essential Services</strong></p>
                            <p style={{ marginLeft: '20px', marginBottom: '8px' }}>Review your selected services and remove those that are nice-to-have rather than essential.</p>
                            
                            <p style={{ marginBottom: '8px' }}><strong>Option 3: Focus on Clinical Care</strong></p>
                            <p style={{ marginLeft: '20px', marginBottom: '0' }}>Clinical Care services have 0% contribution, so they don't count toward your contribution costs. Prioritize these if you need more support.</p>
                        </StrategyCard>
                        
                        <StrategyCard title="Financial Impact" icon={DollarSign} iconColor="amber">
                            <p style={{ marginBottom: '8px' }}>• <strong>Your Contribution</strong>: {formatCurrency(totalContribution)}/year</p>
                            <p style={{ marginBottom: '0' }}>• <strong>Remaining Cashflow</strong>: <strong style={{ color: remainingCashflow >= 0 ? '#10b981' : '#ef4444' }}>{formatCurrency(remainingCashflow)}</strong>/year</p>
                            <p style={{ marginTop: '12px', marginBottom: '0', color: '#ef4444' }}><strong>Important:</strong> Exceeding your budget may require paying the full cost of services above your package level, or discussing options with your provider.</p>
                        </StrategyCard>
                    </>
                );
                
            default:
                return (
                    <p>Welcome! I'm here to help you build your Support at Home service plan.</p>
                );
        }
    };
    
    // Drag handlers
    const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.button !== 0) return; // Only left mouse button
        setIsDragging(true);
        const rect = e.currentTarget.getBoundingClientRect();
        dragRef.current = {
            startX: e.clientX - rect.left,
            startY: e.clientY - rect.top,
            startPosX: panelPos.x,
            startPosY: panelPos.y
        };
    };

    const handleMouseMove = (e: MouseEvent) => {
        if (!isDragging || !dragRef.current) return;
        
        const newX = e.clientX - dragRef.current.startX;
        const newY = e.clientY - dragRef.current.startY;
        
        setPanelPos({
            x: Math.max(0, Math.min(window.innerWidth - 350, newX)),
            y: Math.max(0, Math.min(window.innerHeight - 200, newY))
        });
    };
    
    const handleMouseUp = () => {
        setIsDragging(false);
        dragRef.current = null;
    };
    
    // Add event listeners for dragging
    React.useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isDragging, panelPos]);
    
    if (!showPanel) {
        return (
            <button
                onClick={() => setShowPanel(true)}
                style={{
                    position: 'fixed',
                    top: '20px',
                    left: '20px',
                    background: '#2a4365',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '12px 16px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    zIndex: 10000,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                }}
            >
                💡 Show Insights
            </button>
        );
    }
    
    return (
        <div
            style={{
                position: 'fixed',
                left: panelPos.x,
                top: panelPos.y,
                width: '350px',
                background: 'white',
                border: '2px solid #e2e8f0',
                borderRadius: '12px',
                boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                zIndex: 10000,
                maxHeight: '80vh',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            {/* Header */}
            <div
                style={{
                    cursor: 'move',
                    background: '#f5f7fa',
                    borderTopLeftRadius: '10px',
                    borderTopRightRadius: '10px',
                    padding: '10px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}
                onMouseDown={handleMouseDown}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <img
                        src={agedCareWiseMarkUrl}
                        alt="AgedCareWise"
                        width={18}
                        height={18}
                        style={{ display: 'block', borderRadius: '3px' }}
                    />
                    <span style={{ fontWeight: 700, fontSize: '17px', color: '#2a4365' }}>Service Builder Guide</span>
                </div>
                <button
                    onClick={() => setShowPanel(false)}
                    style={{
                        background: 'none',
                        border: 'none',
                        fontSize: '18px',
                        cursor: 'pointer',
                        color: '#888'
                    }}
                    title="Close"
                >
                    ×
                </button>
            </div>
            
            {/* Content */}
            <div
                ref={contentRef}
                style={{
                    padding: '16px',
                    fontSize: '15px',
                    color: '#222',
                    overflowY: 'auto',
                    flex: 1
                }}
            >
                {getInsightContent()}
            </div>
        </div>
    );
};

export default ServiceBuilderInsightPanel;

